import { Attributes, DateUtils, List, Misc, Rules, Validation } from '@singularsystems/neo-core';
import BankDetail from '../ValueObjects/BankDetail';
import Note from '../Notes/Note';
import INoteListHost from '../Notes/INoteListHost';
import TemporalEntity from '../TemporalEntity';
import AuthorisationValues from '../ValueObjects/AuthorisationValues';
import InvestorBankDetail from './InvestorBankDetail';
import { IPreAuthorisable } from '../IPreAuthorisable';
import { IAuthorisable } from '../IAuthorisable';
import InvestorContact from '../Contacts/InvestorContact';
import { AppService, DomainTypes } from '../../DomainTypes';
import { MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import InvestorFieldConfig from '../Config/FieldConfig/InvestorFieldConfig';
import { isMandatory } from '../Config/FieldConfig/FieldSetting.enum';
import InvestorUser from './InvestorUser';

export default class Investor extends TemporalEntity implements INoteListHost, IAuthorisable, IPreAuthorisable {

    static typeName = "Investor";

    constructor() {
        super();
        this.makeObservable();
    }

    public investorId: number = 0;

    @Rules.Required()
    public legalEntityTypeId: number | null = null;

    @Rules.StringLength(150)
    public investorName: string = "";

    @Rules.StringLength(50)
    public firstName: string = "";

    @Rules.StringLength(50)
    public lastName: string = "";

    @Rules.StringLength(20)
    public shortName: string = "";

    @Attributes.Nullable()
    public titleId: number | null = null;

    @Rules.StringLength(50)
    public idRegNo: string = "";

    @Rules.StringLength(50)
    public taxRefNo: string = "";

    @Rules.StringLength(200)
    public postalAddress: string = "";

    @Rules.StringLength(200)
    public physicalAddress: string = "";

    @Attributes.Nullable()
    public countryId: number | null = null;

    @Attributes.ChildObject(BankDetail, true)
    public bankDetails: BankDetail = new BankDetail();

    @Rules.StringLength(50)
    public withholdingAgentRefNumber: string = "";

    @Rules.StringLength(150)
    public beneficiaryName: string = "";

    @Rules.StringLength(50)
    public beneficiaryTaxReferenceNumber: string = "";

    @Rules.StringLength(200)
    public beneficiaryPhysicalAddress: string = "";

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public dwtExpiryDate: Date | null = null;

    @Attributes.Nullable()
    public dwtExemptionId: number | null = null;

    @Attributes.Nullable()
    @Attributes.Float(10)
    public dwtReducedRate: number | null = null;

    @Attributes.Nullable()
    public portfolioTypeId: number | null = null;

    @Attributes.Nullable()
    public authorisedPersonContactId: number | null = null;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    public authorisedContactName: string | null = null;

    @Attributes.ChildList(InvestorContact)
    public contacts = new List(InvestorContact);

    public users = new List(InvestorUser);

    @Rules.StringLength(10)
    public ledgerAccount: string = "";

    public isRestricted: boolean = false;

    public isLetterPasswordProtected: boolean = false;

    @Attributes.NoTracking(Misc.SerialiseType.Always)
    public isIndividual: boolean = false;

    @Attributes.NoTracking(Misc.SerialiseType.Always)
    public isTrust: boolean = false;

    @Attributes.NoTracking(Misc.SerialiseType.Always)
    public requiresExternalAccountNo: boolean = false;

    @Attributes.ChildObject(AuthorisationValues, true)
    public preAuthorisation: AuthorisationValues = new AuthorisationValues();

    @Attributes.ChildObject(AuthorisationValues, true)
    public authorisation: AuthorisationValues = new AuthorisationValues();

    public isPendingDelete: boolean = false;

    @Rules.StringLength(20)
    public internalAccountNo: string = "";

    @Rules.StringLength(20)
    public externalAccountNo: string = "";

    @Rules.StringLength(50)
    public externalRelationshipManager: string = "";

    public additionalBankDetails = new List(InvestorBankDetail);

    public notes = new List(Note);

    public contactEmails: string = "";

    @Rules.StringLength(20)
    public passportNo: string = "";

    @Attributes.Display("FICA Date")
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public ficaDate: Date | null = null;

    public sanctionScreenPerformed: boolean = false;

    public commission: boolean = false;

    public commissionDiv: boolean = false;

    @Attributes.Integer()
    public requiredClientAuthorisations: number = 0;

    @Attributes.Display("SARS Nature Of Person")
    @Attributes.Nullable()
    public sarsNatureOfPersonId: number | null = null;

    @Attributes.Nullable()
    public investorReferralTypeId: number | null = null;

    @Attributes.Nullable()
    public intraGroupId: number | null = null;

    @Attributes.Nullable()
    public sectorId: number | null = null;

    @Attributes.Nullable()
    public referralContactId: number | null = null;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    public referralContactName: string | null = null;

    // Client only properties / methods
    @Attributes.Serialisation(Misc.SerialiseType.Never)
    public contactEmailsDisplay: string = "";

    public onDeserialiseCompleted() {
        this.contactEmailsDisplay = this.contactEmails.split(';').map(s => s.trim()).join('\n');
    }

    public beforeSerialise(): Investor {
        this.contactEmails = this.contactEmailsDisplay.split('\n').map(s => s.replace(/\s/g, '')).join(';');
        return this;
    }

    public setIdOnNote(note: Note): void {
        note.investorId = this.investorId;
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        const isDigit = (testString: string): boolean => !/[^0-9]/.test(testString);
        const isDigitOrSlash = (testString: string): boolean => !/[^0-9/]/.test(testString);

        rules.failWhen(c => !c.isIndividual && c.investorName.length <= 0, "Investor name is required").onProperties(c => c.investorName);
        rules.failWhen(c => c.isIndividual && c.firstName.length <= 0, "First name is required").onProperties(c => c.firstName);
        rules.failWhen(c => c.isIndividual && c.lastName.length <= 0, "Last name is required").onProperties(c => c.lastName);
        rules.failWhen(c => c.isIndividual && c.titleId == null && this.isRequired("title"), "Title is required").onProperties(c => c.titleId);
        rules.failWhen(c => !isDigitOrSlash(c.taxRefNo), "Tax reference number can only contain numbers");
        rules.failWhen(c => c.dwtReducedRate !== null && !(c.dwtReducedRate >= 0 && c.dwtReducedRate <= 1), "DWT Reduced Rate must be between 0% and 100%");
        rules.failWhen(c => c.dwtExpiryDate != null && c.dwtExpiryDate > DateUtils.today()
            && (c.dwtExemptionId === null && c.dwtReducedRate === null) || (c.dwtExemptionId != null && c.dwtReducedRate != null), "Either a DWT Exemption Code or DWT Reduced Rate must be set, but not both");
        rules.failWhen(c => (c.dwtExpiryDate == null || c.dwtExpiryDate <= DateUtils.today()) && (c.dwtExemptionId != null || c.dwtReducedRate != null), "A valid DWT Expiry must be set in order to set a DWT Exemption Code or DWT Reduced Rate");
        rules.failWhen(c => c.isIndividual && (!c.validIDNo(c.idRegNo) || !isDigit(c.idRegNo)), "Enter a valid ID number.").onProperties(c => c.idRegNo);
        rules.failWhen(c => c.isIndividual && c.idRegNo.length === 0 && c.passportNo.length === 0, "Legal ID Number or Passport Number must be provided").onProperties(c => c.idRegNo);

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.isIndividual && !c.isTrust && !c.idRegNo && this.isRequired("companyRegNo"), "Company reg no. is required.");
        rules.add(Investor.prototype.validateRegNo).onProperties(c => c.idRegNo);
        rules.failWhen(c => c.isTrust && !c.idRegNo && this.isRequired("mastersRefNo"), "Masters ref no. is required.");
        rules.failWhen(c => !c.internalAccountNo && this.isRequired("internalAccountNo"), "Please enter a value.");
        rules.failWhen(c => !c.externalAccountNo && (c.requiresExternalAccountNo || this.isRequired("externalAccountNo")), "External account no. is required.");
        rules.failWhen(c => !c.taxRefNo && this.isRequired("taxRefNo"), "Tax ref no. is required.");
        rules.failWhen(c => !c.postalAddress && this.isRequired("postalAddress"), "Postal address is required.");
        rules.failWhen(c => !c.physicalAddress && this.isRequired("physicalAddress"), "Physical address is required.");
        rules.failWhen(c => !c.countryId && this.isRequired("countryId"), "Country is required.");
        rules.failWhen(c => c.additionalBankDetails.length === 0 && this.isRequired("additionalBankDetails"), "Additional bank details are required.");
        rules.failWhen(c => !c.authorisedPersonContactId && this.isRequired("authorisedPersonContactId"), "Authorised contact is required.");
        rules.failWhen(c => c.contacts.length === 0 && this.isRequired("contacts"), "At least 1 linked contact is required.");
        rules.failWhen(c => !c.contactEmails && this.isRequired("contactEmails"), "Contact email is required");
        rules.failWhen(c => !c.withholdingAgentRefNumber && this.isRequired("withholdingAgentRefNumber"), "Withholding agent ref number is required.");
        rules.failWhen(c => !c.beneficiaryName && this.isRequired("beneficiaryName"), "Beneficiary name is required.");
        rules.failWhen(c => !c.beneficiaryTaxReferenceNumber && this.isRequired("beneficiaryTaxReferenceNumber"), "Beneficiary tax reference number is required.");
        rules.failWhen(c => !c.beneficiaryPhysicalAddress && this.isRequired("beneficiaryPhysicalAddress"), "Beneficiary physical address is required.");
        rules.failWhen(c => !c.portfolioTypeId && this.isRequired("portfolioTypeId"), "Portfolio type is required.");
        rules.failWhen(c => !c.ficaDate && this.isRequired("ficaDate"), "Fica date is required.");
        rules.failWhen(c => !c.ledgerAccount && this.isRequired("ledgerAccount"), "Ledger account is required.");
        rules.failWhen(c => !c.investorReferralTypeId && this.isRequired("investorReferralTypeId"), "Investor Referral Type is required.");

        rules.failWhen(c => c.requiredClientAuthorisations > 0 && c.requiredClientAuthorisations >= c.users.length, c => `At least ${c.requiredClientAuthorisations + 1} users are required.`);
    }

    private validateRegNo(context: Validation.IRuleContext) {
        const slashChar = "/";
        const validLastDigits = ["06", "07", "08", "09", "10", "11", "20", "21", "22", "23", "24", "25", "26"];

        if (!this.isIndividual && !this.isTrust && this.idRegNo.length > 0) {
            if (this.idRegNo.length === 14) {
                const yearAndCentury = parseInt(this.idRegNo.substring(0, 4), 10);
                if (yearAndCentury >= 1800 && yearAndCentury <= new Date().getFullYear()) {
                    // Check for slashes in expected positions
                    const validSlashes = this.idRegNo.substring(4, 5) === slashChar && this.idRegNo.substring(11, 12) === slashChar;
                    if (validSlashes && validLastDigits.includes(this.idRegNo.substring(12, 14))) {
                        if (this.idRegNo.replace(new RegExp(slashChar, 'g'), '').split('').every(char => /\d/.test(char))) {
                            return;
                        }
                    }
                }
            }

            context.addWarning("Company registration number does not appear to be valid.");
        }
    }

    public toString(): string {
        if (this.isNew || !this.investorName) {
            return "New investor";
        } else {
            return this.investorName ? this.investorName : `${this.firstName} ${this.lastName}`;
        }
    }

    private validIDNo(idRegNo: string) {
        if (this.isIndividual && idRegNo.length > 0) {
            if (idRegNo.length !== 13) {
                return false;
            }
        }
        return true;
    }

    public formatCompanyRegNo() {
        if (this! && !this.isTrust && !this.isIndividual && this.idRegNo.length == 12) {
            const part1 = this.idRegNo.slice(0, 4);
            const part2 = this.idRegNo.slice(4, 10);
            const part3 = this.idRegNo.slice(10);

            this.idRegNo = `${part1}/${part2}/${part3}`;
        }
    }

    private isRequired(fieldName: keyof InvestorFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.investorFields[fieldName] as MandatoryAndVisibleSetting);
    }
}
